/**
 * 公共模块 - 与主窗口通信
 * 参照 zhaofu_excel_typescript/www/common/module.ts
 * 
 * 提供默认的 Actions 实现，业务页面只需要覆盖特殊逻辑
 */

declare const mini: any;
declare const $: any;

// 工具栏状态管理类
class ToolBar {
    private _add: boolean = false;
    private _remove: boolean = false;
    private _save: boolean = false;
    private _printReport: boolean = false;
    private _search: boolean = false;
    private _exportcsv: boolean = false;
    private _uploadExcel: boolean = false;

    private _authadd: boolean = true;
    private _authremove: boolean = true;
    private _authsave: boolean = true;
    private _authprintReport: boolean = true;
    private _authsearch: boolean = true;
    private _authexportcsv: boolean = true;
    private _authuploadExcel: boolean = true;
    private _timer: number | undefined = undefined;

    // 发送消息给主窗体,更新当前toolbar状态
    refreshToolBar() {
        if (!this._timer) {
            this._timer = window.setTimeout(() => {
                const msg = {
                    type: 'RefreshToolbar',
                    ADD: this._add,
                    REMOVE: this._remove,
                    SAVE: this._save,
                    SEARCH: this._search,
                    PRINTREPORT: this._printReport,
                    EXPORTCSV: this._exportcsv,
                    UPLOADEXCEL: this._uploadExcel,
                };
                window.top!.postMessage(msg, "*");
                clearTimeout(this._timer);
                this._timer = undefined;
            }, 1);
        }
    }

    set add(value: boolean) {
        if (this._authadd) {
            this._add = value;
            this.refreshToolBar();
        }
    }
    get add() {
        return this._add;
    }

    set remove(value: boolean) {
        if (this._authremove) {
            this._remove = value;
            this.refreshToolBar();
        }
    }
    get remove() {
        return this._remove;
    }

    set save(value: boolean) {
        if (this._authsave) {
            this._save = value;
            this.refreshToolBar();
        }
    }
    get save() {
        return this._save;
    }

    set printReport(value: boolean) {
        if (this._authprintReport) {
            this._printReport = value;
            this.refreshToolBar();
        }
    }
    get printReport() {
        return this._printReport;
    }

    set search(value: boolean) {
        if (this._authsearch) {
            this._search = value;
            this.refreshToolBar();
        }
    }
    get search() {
        return this._search;
    }

    set exportcsv(value: boolean) {
        if (this._authexportcsv) {
            this._exportcsv = value;
            this.refreshToolBar();
        }
    }
    get exportcsv(): boolean {
        return this._exportcsv;
    }

    set uploadExcel(value: boolean) {
        if (this._authuploadExcel) {
            this._uploadExcel = value;
            this.refreshToolBar();
        }
    }
    get uploadExcel(): boolean {
        return this._uploadExcel;
    }

    // 初始化toolbar
    init({ ADD, REMOVE, SAVE, PRINTREPORT, SEARCH, EXPORTCSV, UPLOADEXCEL }: any) {
        SEARCH = SEARCH === undefined || SEARCH;
        this._add = ADD;
        this._authadd = ADD;
        this._remove = REMOVE;
        this._authremove = REMOVE;
        this._save = SAVE;
        this._authsave = SAVE;
        this._printReport = PRINTREPORT;
        this._authprintReport = PRINTREPORT;
        this._search = SEARCH;
        this._authsearch = SEARCH;
        this._exportcsv = EXPORTCSV;
        this._authexportcsv = EXPORTCSV;
        this._uploadExcel = UPLOADEXCEL;
        this._authuploadExcel = UPLOADEXCEL;
    }
}

const toolbar = new ToolBar();

// 模块动作回调接口
type TAction_CB = (() => void) | null;
interface IAction {
    add: TAction_CB;
    remove: TAction_CB;
    save: TAction_CB;
    search: TAction_CB;
    printReport: TAction_CB;
    toolbarScan: TAction_CB;
    permissionSetting: ((e: any) => void) | null;
    active: TAction_CB;
    exportcsv: TAction_CB;
    uploadExcel: TAction_CB;
    [prop: string]: TAction_CB | any;
}

// 页面配置接口 - 用于默认行为
interface IPageConfig {
    dg?: any;                    // 主 DataGrid
    saveUrl?: string;            // 保存接口 URL
    keyField?: string;           // 主键字段（用于控制编辑）
    defaultAddValues?: any;      // 新增行的默认值
}

// 页面配置
let pageConfig: IPageConfig = {};

/**
 * 统一消息提示（无动画版）
 * @param content 提示内容
 * @param state 状态类型：success | info | warning | danger
 */
let _lastTipContent = '';
let _lastTipState = '';
let _lastTipTime = 0;
let _errorTips: any[] = []; // 保存未关闭的错误提示框

function showMessage(content: string, state: 'success' | 'info' | 'warning' | 'danger' | 'error' = 'success') {
    if (typeof mini === 'undefined') return;
    const now = Date.now();
    if (_lastTipContent === content && _lastTipState === state && now - _lastTipTime < 3000) {
        return; // 3 秒内相同内容的提示去重
    }
    _lastTipContent = content;
    _lastTipState = state;
    _lastTipTime = now;

    const isError = state === 'danger' || state === 'error';

    // 非错误提示出现时，先关闭所有未消失的错误提示框
    if (!isError && _errorTips.length > 0) {
        _errorTips.forEach(function (tip) {
            if (tip && tip.remove) tip.remove();
        });
        _errorTips = [];
    }

    const cls = 'mini-tips mini-tips-' + state;
    const style = isError ? 'cursor:pointer;' : '';
    const title = isError ? 'title="双击关闭"' : '';
    const html = '<div class="' + cls + '" style="' + style + '" ' + title + '>' + content + '</div>';
    const jq = jQuery(html).appendTo(document.body);

    mini.showAt({
        el: jq[0],
        x: 'center',
        y: 'top',
        offset: [0, 6],
        fixed: true
    });

    if (isError) {
        // 错误提示：不自动消失，双击关闭
        _errorTips.push(jq);
        jq.on('dblclick', function () {
            jq.remove();
            const idx = _errorTips.indexOf(jq);
            if (idx > -1) _errorTips.splice(idx, 1);
        });
    } else {
        // 其他提示：2 秒后自动消失
        setTimeout(() => {
            jq.remove();
        }, 2000);
    }
}

// 设置页面配置
function setPageConfig(config: IPageConfig) {
    pageConfig = config;
    // 初始化 grid_edit_services
    if (config.dg && config.keyField) {
        defaultActions.initGrid(config.dg, config.keyField);
    }
}

// 默认 Actions 实现
const defaultActions = {
    // 初始化 DataGrid
    initGrid(grid: any, keyField: string) {
        // 加载前检查是否有未保存的数据
        grid.on("beforeload", (e: any) => {
            grid.commitEdit();
            if (grid.getChanges().length > 0) {
                mini.showMessageBox({
                    title: "提示",
                    message: "数据已经修改且未保存，是否继续？",
                    iconCls: "mini-messagebox-question",
                    buttons: ["ok", "no"],
                    callback: (b: string) => {
                        if (b == "ok") {
                            grid.clearData();
                            grid.load(e.params);
                        }
                    }
                });
                e.cancel = true;
            }
        });

        // 控制主键字段不可编辑
        grid.on("cellbeginedit", (e: any) => {
            if (e.field === keyField && e.record._state != "added") {
                e.cancel = true;
            }
        });

        // 数据变化时自动扫描工具栏
        grid.on("load", () => defaultActions.toolbarScan());
        grid.on("addrow", () => defaultActions.toolbarScan());
        grid.on("removerow", () => defaultActions.toolbarScan());
        grid.on("updaterow", () => defaultActions.toolbarScan());
    },

    // 默认增加
    add: () => {
        const dg = pageConfig.dg;
        if (!dg) {
            console.warn("[defaultActions.add] 未配置 DataGrid");
            return;
        }
        const newRow: any = pageConfig.defaultAddValues ? { ...pageConfig.defaultAddValues } : {};
        dg.addRow(newRow, 0);
        dg.deselectAll(false);
        dg.select(newRow);
        dg.beginEditCell(newRow, 0);
    },

    // 默认删除
    remove: () => {
        const dg = pageConfig.dg;
        if (!dg) {
            console.warn("[defaultActions.remove] 未配置 DataGrid");
            return;
        }
        const rows = dg.getSelecteds();
        if (rows.length == 0) {
            mini.alert("请选择要删除的记录");
            return;
        }
        const rowIndex = dg.indexOf(rows[0]);
        const displayIndex = rowIndex >= 0 ? rowIndex + 1 : rows[0]._index || 1;
        
        mini.confirm("确定删除选中第 " + displayIndex + " 行记录吗？", "确认删除", (action: string) => {
            if (action == "ok") {
                dg.removeRows(rows);
                
                // 删除后自动选中下一行，没有则选前一行
                const data = dg.getData();
                if (data.length > 0) {
                    let nextRow = dg.getRow(rowIndex);
                    if (!nextRow) {
                        nextRow = dg.getRow(rowIndex - 1);
                    }
                    if (nextRow) {
                        dg.select(nextRow);
                    }
                }
            }
        });
    },

    // 默认保存
    save: () => {
        const dg = pageConfig.dg;
        const url = pageConfig.saveUrl;
        if (!dg || !url) {
            console.warn("[defaultActions.save] 未配置 DataGrid 或 saveUrl");
            return;
        }
        dg.commitEdit();
        dg.validate();
        if (dg.isValid() == false) {
            const error = dg.getCellErrors()[0];
            dg.beginEditCell(error.record, error.column);
            return;
        }

        const changes = dg.getChanges();
        if (changes.length == 0) return;
        const json = mini.encode(changes);

        dg.loading("保存中，请稍后......");
        $.ajax({
            url: url,
            data: { data: json },
            type: "post",
            success: (text: string) => {
                const result = mini.decode(text);
                console.log("[module] save success callback, result:", result);
                if (result.success) {
                    dg.accept();
                    showMessage("保存成功!", "success");
                } else {
                    showMessage("保存失败：" + result.message, "danger");
                }
                dg.unmask();
            },
            error: (xhr: any, status: string, err: string) => {
                handleAjaxError(xhr, status, err, '保存失败');
                dg.unmask();
            }
        });
    },

    // 默认查询
    search: () => {
        const dg = pageConfig.dg;
        if (!dg) {
            console.warn("[defaultActions.search] 未配置 DataGrid");
            return;
        }
        dg.load({});
    },

    // 默认导出 CSV
    exportcsv: () => {
        const dg = pageConfig.dg;
        if (!dg) {
            console.warn("[defaultActions.exportcsv] 未配置 DataGrid");
            return;
        }
        const data = getCSVData(dg, "数据导出");
        download(data);
        showMessage("导出成功!", "success");
    },

    // 默认工具栏扫描
    toolbarScan: () => {
        const dg = pageConfig.dg;
        if (!dg) return;
        const hasChanges = dg.getChanges().length > 0;
        toolbar.save = hasChanges;
        toolbar.add = true;
        toolbar.remove = true;
        toolbar.search = true;
        toolbar.exportcsv = true;
        toolbar.printReport = false;
    }
};

// Actions 对象 - 业务页面可覆盖
const Actions: IAction = {
    add: null,              // 当工具栏按下"增加"按钮时触发
    remove: null,           // 当工具栏按下"删除"按钮时触发
    save: null,             // 当工具栏按下"保存"按钮时触发
    search: null,           // 当工具栏按下"查询"按钮时触发
    printReport: null,      // 当工具栏按下"打印"按钮时触发
    exportcsv: null,        // 当工具栏按下"导出到Excel"按钮时触发
    uploadExcel: null,      // 当工具栏按下"导入Excel"按钮时触发
    toolbarScan: null,      // 当模块内所有的DataGrid或TreeGrid状态变化时触发
    permissionSetting: null,// 当模块加载完成,系统给模块赋予权限时触发
    active: null,           // 当模块变为当前模块时触发
};

// 模块消息处理
window.addEventListener("message", (e: MessageEvent) => {
    const msg = e.data;
    switch (msg.type) {
        case "SetPermission":
            if (!msg.SAVE) {
                msg.ADD = false;
                msg.REMOVE = false;
            }
            Actions.permissionSetting ? Actions.permissionSetting(msg) : void 0;
            toolbar.init(msg);
            break;
        case "Active":
            // 优先调用页面的 toolbarScan，如果没有则使用默认
            if (Actions.toolbarScan) {
                Actions.toolbarScan();
            } else {
                defaultActions.toolbarScan();
            }
            toolbar.refreshToolBar();
            Actions.active ? Actions.active() : void 0;
            break;
        case "Action":
            // 优先调用页面的实现，如果没有则使用默认
            const action = msg.action as keyof IAction;
            if (Actions[action]) {
                (Actions[action] as any)();
            } else if ((defaultActions as any)[action]) {
                (defaultActions as any)[action]();
            }
            break;
        default:
            break;
    }
});

// 导出Excel辅助函数
function getCSVData(dg: any, titleStr: string) {
    dg.commitEdit();
    const field: string[] = [];
    const visibleFlag: boolean[] = [];
    const dateFlag: string[] = [];
    const rendererFunc: (((e: any) => string) | undefined)[] = [];
    const columns = dg.columns;
    const data = dg.getVisibleRows();
    const result: any[] & { title?: string } = new Array(data.length + 1);
    for (let i = 0; i < result.length; i++) {
        result[i] = new Array();
    }

    // header
    let b;
    for (let i = 0; i < columns.length; i++) {
        let column = columns[i];
        if (column.type == 'indexcolumn') {
            b = true;
        } else {
            b = false;
        }

        visibleFlag.push(column.visible);
        dateFlag.push(column.dateFormat);
        rendererFunc.push(column.renderer);
        if (b) {
            field.push('index');
        } else {
            field.push(column.field);
        }

        if (column.visible) {
            if (b) {
                result[0].push('');
            } else {
                let a = column.header.replace('\n', '');
                a = a.replace(/\s+/g, '');
                result[0].push(a);
            }
        }
    }
    
    // data
    let v;
    for (let i = 0; i < data.length; i++) {
        for (let j = 0; j < field.length; j++) {
            if (visibleFlag[j]) {
                let fValue = data[i][field[j]];
                if (field[j] == 'index') {
                    v = i + 1;
                } else if (dateFlag[j] != undefined) {
                    v = mini.formatDate(fValue, "yyyy-MM-dd hh:mm:ss");
                } else if (rendererFunc[j]) {
                    v = rendererFunc[j]!.call(undefined, { value: fValue });
                } else if (fValue == null) {
                    v = "";
                } else {
                    v = fValue;
                }
                if (typeof (v) == "string") {
                    v = (v + "\t");
                }
                result[i + 1].push(v);
            }
        }
    }

    let timeStr = new Date().toLocaleString();
    timeStr = timeStr.replace(/\//g, '');
    timeStr = timeStr.replace(/:/g, '');
    timeStr = timeStr.replace(/ /g, '');
    result.title = titleStr + " " + timeStr;
    return result;
}

function download(data: any[] & { title?: string }) {
    let buf = "";
    let row;
    for (row of data) {
        buf = buf + row.join(",");
        buf += "\n";
    }
    let utf8 = new TextEncoder().encode(buf);
    let withbom = new Uint8Array(3 + utf8.length);
    withbom.set([0xEF, 0xBB, 0xBF]);
    withbom.set(utf8, 3);
    const url = URL.createObjectURL(new Blob([withbom], { type: 'text/csv' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = data.title!;
    link.click();
}

// 将 Actions 和 defaultActions 挂载到 window，使主窗口可以访问
if (typeof window !== 'undefined') {
    (window as any).Actions = Actions;
    (window as any).defaultActions = defaultActions;
    (window as any).setPageConfig = setPageConfig;
}

/**
 * 统一处理 ajax error，提取服务器返回的真实错误信息
 * @param xhr jqXHR 对象
 * @param status 状态字符串
 * @param err 错误描述
 * @param prefix 提示前缀，如"保存失败"、"下达失败"
 */
function handleAjaxError(xhr: any, status: string, err: string, prefix: string = '操作失败') {
    let detail = '';
    const response = xhr && xhr.responseText ? xhr.responseText : '';

    if (!response) {
        detail = '服务器无响应';
    } else {
        // 先尝试解析 JSON
        try {
            const json = JSON.parse(response);
            if (json && json.message) {
                detail = json.message;
            } else {
                detail = JSON.stringify(json).substring(0, 200);
            }
        } catch (e) {
            // 不是 JSON，可能是 PHP 默认 HTML 错误页
            // 尝试提取 <b>XXX</b>: ...
            const match = response.match(/<b>([^<]+)<\/b>[:\s]*(.+?)(?:<br|<\/)/i);
            if (match) {
                detail = match[1] + ': ' + match[2].trim();
            } else {
                // 兜底：显示原始响应前200字符（去掉HTML标签）
                detail = response.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().substring(0, 200);
            }
        }
    }

    console.error(`[Ajax Error] ${prefix}:`, status, err, detail);
    showMessage(prefix + '：' + detail, 'danger');
}

export {
    toolbar,
    Actions,
    setPageConfig,
    defaultActions,
    getCSVData,
    download,
    showMessage,
    handleAjaxError,
};
